import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

// Snowflake animation
const snowfall = keyframes`
  0% {
    transform: translateY(-100%) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(360deg);
    opacity: 0.8;
  }
`;

// Marquee scroll animation
const scroll = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
`;

// Pulse animation for icons
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
`;

// Glow animation
const glow = keyframes`
  0%, 100% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.8),
                 0 0 20px rgba(255, 215, 0, 0.6),
                 0 0 30px rgba(255, 215, 0, 0.4);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 255, 255, 1),
                 0 0 30px rgba(255, 215, 0, 0.8),
                 0 0 40px rgba(255, 215, 0, 0.6);
  }
`;

const BannerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  background: 'linear-gradient(135deg, #c31432 0%, #240b36 100%)',
  overflow: 'hidden',
  padding: '12px 0',
  borderBottom: '3px solid #FFD700',
  boxShadow: '0 4px 20px rgba(195, 20, 50, 0.4)',
  zIndex: 1000,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255, 255, 255, 0.05) 10px, rgba(255, 255, 255, 0.05) 20px)',
    pointerEvents: 'none',
  }
}));

const Snowflake = styled(Box)(({ delay, duration, left }) => ({
  position: 'absolute',
  top: '-10px',
  left: `${left}%`,
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '20px',
  animation: `${snowfall} ${duration}s linear infinite`,
  animationDelay: `${delay}s`,
  pointerEvents: 'none',
  userSelect: 'none',
}));

const MarqueeWrapper = styled(Box)({
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
});

const MarqueeContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  animation: `${scroll} 15s linear infinite`,
  [theme.breakpoints.down('sm')]: {
    animation: `${scroll} 6s linear infinite`,
  },
  '&:hover': {
    animationPlayState: 'paused',
  }
}));

const OfferText = styled(Typography)(({ theme }) => ({
  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
  fontWeight: 800,
  color: '#FFFFFF',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginRight: '40px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  animation: `${glow} 2s ease-in-out infinite`,
  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    letterSpacing: '1px',
    marginRight: '30px',
  }
}));

const CodeBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  color: '#c31432',
  padding: '8px 20px',
  borderRadius: '12px',
  fontWeight: 900,
  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' },
  border: '3px dashed #FFFFFF',
  boxShadow: '0 4px 15px rgba(255, 215, 0, 0.5)',
  letterSpacing: '3px',
  animation: `${pulse} 1.5s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '6px 15px',
    letterSpacing: '2px',
  }
}));

const IconWrapper = styled(Box)({
  display: 'inline-flex',
  animation: `${pulse} 2s ease-in-out infinite`,
  color: '#FFD700',
});

const ChristmasOfferBanner = () => {
  // Generate random snowflakes
  const snowflakes = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    left: Math.random() * 100,
    symbol: ['❄', '❅', '❆'][Math.floor(Math.random() * 3)]
  }));

  return (
    <BannerContainer>
      {/* Snowflakes */}
      {snowflakes.map(flake => (
        <Snowflake 
          key={flake.id}
          delay={flake.delay}
          duration={flake.duration}
          left={flake.left}
        >
          {flake.symbol}
        </Snowflake>
      ))}

      {/* Marquee Content */}
      <MarqueeWrapper>
        <MarqueeContent>
          {/* Repeat the content - fewer reps on mobile for faster display */}
          {[1, 2].map((item) => (
            <React.Fragment key={item}>
              {/* Show code FIRST on mobile for immediate visibility */}
              <OfferText>
                🎄 Use Code: <CodeBox component="span">SANTA2025</CodeBox> for 15% OFF! 🎁
              </OfferText>
              
              <OfferText>
                <IconWrapper>
                  <CardGiftcardIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                </IconWrapper>
                Year-End Christmas Sale! 🎅
              </OfferText>
              
              <OfferText sx={{ marginRight: '60px' }}>
                🌟 Limited Time Offer - Perfect Gift for Young Minds! 🎊
              </OfferText>
            </React.Fragment>
          ))}
        </MarqueeContent>
      </MarqueeWrapper>
    </BannerContainer>
  );
};

export default ChristmasOfferBanner;
